import Button from "../Button/Button";
import LeftArrowIcon from "../../assets/svg/left-arrow-icon";
import { UserData } from "../../utils/global-types/global-types";
import { OutlookIntegration } from "../../redux/slices/authSlice";
import { Avatar } from "../Avatar/Avatar";
import AddIcon from "../../assets/svg/add-icon";
import Input from "../Input/Input";
import OutlookImage from "../../assets/images/Outlook.png";
import CircularLoader from "../Loader/CircularLoader";
import CrossIcon from "../../assets/svg/cross-icon";
import { OutlookConnectStatus } from "./ProfileCard";
import ThreeDotsVerticalIcon from "../../assets/svg/three-dots-vertical-icon";
import { useOnClickOutside } from "../../hooks/useOnClickOutside";
import DisconnectOutlookModal from "./DisconnectOutlookModal";
import PhoneNumberCountryCodeSelector from "../PhoneNumberCountryCodeSelector/PhoneNumberCountryCodeSelector";
import { countryOptions } from "../../utils/constants/countryOptions";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useUpdateProfileMutation } from "../../redux/services/authApi";
import { useRef, useState, useEffect } from "react";
import parsePhoneNumberFromString from "libphonenumber-js";
import { customToast } from "../../utils/custom-toast/custom-toast";
import {
  transformCountryOptions,
  sortCountryOptions,
  findCountryByCode,
  formatPhoneNumber,
} from "../PhoneNumberCountryCodeSelector/utils/phoneNumberUtils";
import { CountryOption } from "../PhoneNumberCountryCodeSelector/utils/types";
import { login } from "../../redux/slices/authSlice";
import { useDispatch, useSelector } from "react-redux";
import { getRegionFlag } from "../../utils/constants/regionDefaultCallingCodes";
import { RootState } from "../../redux/store";

const ProfileSchema = Yup.object().shape({
  name: Yup.string()
    .required("Name is required")
    .min(2, "Name must be at least 2 characters")
    .max(50, "Name cannot exceed 50 characters"),
  phone_number: Yup.string()
    .required("Phone number is required")
    .test(
      "is-valid-with-country",
      "Invalid phone number format",
      function (value) {
        if (!this?.parent) return false;
        const { phoneCountry } = this.parent;
        if (!value || !phoneCountry?.dialCode) return false;
        try {
          const phoneNumber = parsePhoneNumberFromString(
            `${phoneCountry.dialCode}${value}`
          );
          return phoneNumber ? phoneNumber?.isValid?.() : false;
        } catch {
          return false;
        }
      }
    ),
  phoneCountry: Yup.object().required("Country code is required").nullable(),
  email: Yup.string().email("Invalid email format"),
  profile_picture: Yup.mixed().optional(),
});

const EditProfile = ({
  setShowEditProfile,
  userData,
  handleConnectOutlook,
  outlookIntegration,
  outlookConnectStatus,
  showDisconnectModal,
  setShowDisconnectModal,
  handleDisconnectOutlook,
  isDisconnecting,
}: {
  setShowEditProfile: (show: boolean) => void;
  userData: UserData;
  handleConnectOutlook: () => void;
  outlookIntegration: OutlookIntegration | null;
  outlookConnectStatus: OutlookConnectStatus;
  showDisconnectModal: boolean;
  setShowDisconnectModal: (show: boolean) => void;
  handleDisconnectOutlook: () => void;
  isDisconnecting: boolean;
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const threeDotMenuRef = useRef<HTMLDivElement>(null);
  const [showThreeDotMenu, setShowThreeDotMenu] = useState(false);
  const [updateProfile, { isLoading }] = useUpdateProfileMutation();

  useOnClickOutside(threeDotMenuRef, () => setShowThreeDotMenu(false));
  const [phoneInput, setPhoneInput] = useState("");
  const dispatch = useDispatch();
  const client = useSelector((state: RootState) => state?.auth?.client);

  const transformedCountryOptions = transformCountryOptions(countryOptions);
  const sortedCountryOptions = sortCountryOptions(transformedCountryOptions);

  // Find the initial country based on country_code
  const getInitialPhoneCountry = () => {
    if (userData?.country_code) {
      const countryCode = userData.country_code.replace("+", "");
      return (
        findCountryByCode(countryCode, transformedCountryOptions) ||
        transformedCountryOptions.find(
          (c) => c.dialCode === userData.country_code
        ) ||
        null
      );
    }
    return findCountryByCode(
      getRegionFlag(client?.region),
      transformedCountryOptions
    );
  };

  const getUpdatedFields = (values: {
    name: string;
    role: string;
    email: string;
    phone_number: string;
    phoneCountry: CountryOption | null;
    profile_picture: string | File;
    _id: string;
  }) => {
    const updatedFields: Partial<UserData> & { profile_picture?: File } = {};

    if (values.name !== userData.name) {
      updatedFields.name = values.name;
    }
    if (values.email !== userData.email) {
      updatedFields.email = values.email;
    }
    if (values.phone_number !== userData.phone_number) {
      updatedFields.phone_number = values.phone_number;
    }
    if (values.phoneCountry?.dialCode !== userData.country_code) {
      updatedFields.country_code = values.phoneCountry?.dialCode || "";
    }
    if (values.profile_picture instanceof File) {
      updatedFields.profile_picture = values.profile_picture;
    }

    return updatedFields;
  };

  const handleSubmit = async (values: {
    name: string;
    role: string;
    email: string;
    phone_number: string;
    phoneCountry: CountryOption | null;
    profile_picture: string | File;
    _id: string;
  }) => {
    const updatedFields = getUpdatedFields(values);

    const formData = new FormData();
    formData.append("data", JSON.stringify(updatedFields));

    if (
      values?.profile_picture &&
      typeof values?.profile_picture !== "string" &&
      values?.profile_picture instanceof File
    ) {
      formData.append("image", values?.profile_picture);
    }

    try {
      const response = await updateProfile?.({
        userId: userData?._id || "",
        formData,
      })?.unwrap?.();
      dispatch(
        login({
          user: {
            ...userData,
            _id: response?._id,
            name: response?.name ?? null,
            email: response?.email,
            phone_number: response?.phone_number ?? null,
            profile_picture: response?.profile_picture ?? null,
            country_code: response?.country_code ?? null,
          },
        })
      );
      customToast("Profile updated successfully", "success");
      setShowEditProfile(false);
    } catch (error: unknown) {
      const err = error as { data?: { detail: string } };
      customToast(err?.data?.detail || "Failed to update profile", "error");
    }
  };

  const {
    values,
    errors,
    touched,
    setFieldValue,
    handleSubmit: formikHandleSubmit,
    isSubmitting,
  } = useFormik({
    initialValues: {
      name: userData?.name || "",
      role: userData?.role || "",
      email: userData?.email || "",
      phone_number: userData?.phone_number || "",
      phoneCountry: getInitialPhoneCountry(),
      profile_picture: userData?.profile_picture || "",
      _id: userData?._id || "",
    },
    validationSchema: ProfileSchema,
    onSubmit: handleSubmit,
  });

  useEffect(() => {
    if (userData?.phone_number && values.phoneCountry) {
      const formattedPhone = formatPhoneNumber(
        userData.phone_number,
        values.phoneCountry
      );
      setPhoneInput(formattedPhone);
    }
  }, [userData?.phone_number, values.phoneCountry]);

  const handlePhoneInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    const digitsOnly = inputValue.replace(/\D/g, "");
    const formattedNumber = formatPhoneNumber(digitsOnly, values.phoneCountry);
    setPhoneInput(formattedNumber);
    setFieldValue("phone_number", digitsOnly);
  };

  const handlePhoneCountryChange = (country: CountryOption) => {
    setFieldValue("phoneCountry", country);

    if (phoneInput) {
      const digitsOnly = phoneInput.replace(/\D/g, "");
      const formattedNumber = formatPhoneNumber(digitsOnly, country);
      setPhoneInput(formattedNumber);
      setFieldValue("phone_number", digitsOnly);
    }
  };

  return (
    <div className="flex flex-col gap-4 p-3">
      <div className="flex gap-2 justify-start">
        <Button
          innerText="Go back"
          Icon={LeftArrowIcon}
          onClick={() => setShowEditProfile(false)}
          iconLeft
          buttonContainerClass="self-start"
          iconClass="w-[14px] h-[14px] text-verba-indigo dark:text-verba-indigo-dk"
          innerTextClass="text-xs text-heading-text dark:text-heading-text-dk font-medium"
        />
      </div>

      <div className="flex justify-between items-center w-fit">
        {values?.profile_picture ? (
          <img
            src={
              typeof values?.profile_picture === "string"
                ? values?.profile_picture
                : URL.createObjectURL(values?.profile_picture)
            }
            alt="profile_picture"
            className="object-cover rounded-full w-[48px] h-[48px]"
          />
        ) : (
          <Avatar text={values?.name?.slice(0, 2)} size={48} />
        )}
        <input
          id="profile-picture"
          name="profile_picture"
          type="file"
          ref={fileInputRef}
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const file = e?.target?.files?.[0];
            if (file) {
              setFieldValue("profile_picture", file);
              if (fileInputRef?.current) {
                fileInputRef.current.value = "";
              }
            }
          }}
        />
        <Button
          innerText="Upload Photo"
          iconLeft
          Icon={AddIcon}
          iconClass="w-3.5 h-3.5 text-verba-blue dark:text-verba-blue-dk"
          innerTextClass="text-sm text-verba-blue dark:text-verba-blue-dk"
          onClick={() => fileInputRef?.current?.click?.()}
        />
      </div>

      <form onSubmit={formikHandleSubmit} className="flex flex-col gap-4">
        <Input
          label="Name"
          name="name"
          value={values?.name}
          error={touched?.name && !!errors?.name}
          errorMessage={touched?.name ? errors?.name : ""}
          labelClassName="text-sm text-verba-field-label dark:text-verba-field-label-dk font-normal"
          secondaryContainerClassName="w-full p-2 border border-quaternary-gray-lt bg-pure-white dark:bg-pure-white-dk rounded-lg text-primary-blue-dk text-sm focus:outline-none dark:text-primary-gray-lightest dark:border-none"
          onChange={(e) => setFieldValue("name", e.target.value)}
        />
        <Input
          label="Role"
          name="role"
          value={values?.role}
          disabled={true}
          labelClassName="text-sm text-verba-field-label dark:text-verba-field-label-dk font-normal"
          secondaryContainerClassName="w-full p-2 border border-quaternary-gray-lt bg-pure-white dark:bg-pure-white-dk rounded-lg text-primary-blue-dk text-sm focus:outline-none dark:text-primary-gray-lightest dark:border-none opacity-50"
        />
        <Input
          label="Email"
          name="email"
          value={values?.email}
          disabled={true}
          labelClassName="text-sm text-verba-field-label dark:text-verba-field-label-dk font-normal"
          secondaryContainerClassName="w-full p-2 border border-quaternary-gray-lt bg-pure-white dark:bg-pure-white-dk rounded-lg text-primary-blue-dk text-sm focus:outline-none dark:text-primary-gray-lightest dark:border-none opacity-50"
        />
        <PhoneNumberCountryCodeSelector
          label="Phone Number"
          labelClassName="text-verba-field-label dark:text-verba-field-label-dk font-normal text-md"
          required={true}
          countryOptions={sortedCountryOptions}
          selectedCountry={values.phoneCountry}
          onCountryChange={handlePhoneCountryChange}
          phoneInput={phoneInput}
          onPhoneInputChange={handlePhoneInputChange}
          validationError={touched.phone_number ? errors.phone_number : ""}
          containerClassName="w-full"
          countryDropdownClassName="w-[250px] min-w-[250px] max-h-[250px]"
          countryDropdownSelectedOptionClassName="h-[34px] px-[8px] py-[9px]"
          phoneInputClassName="h-[36px] px-[8px] py-[6px] rounded-[6px] bg-popper-bg dark:bg-popper-bg-dk border border-border-gray dark:border-border-gray-dk"
          countryDropdownPlaceholder="Select Country"
          countryDropdownSearchPlaceholder="Search countries..."
          countryDropdownSectionHeading="All Countries"
        />
        {outlookIntegration?.is_integration_available && (
          <>
            <div className="border-t border-gray-25 dark:border-gray-25-dk my-1" />
            <div className="flex gap-2.5 items-start bg-pure-white dark:bg-pure-white-dk border border-border-gray dark:border-border-gray-dk rounded-lg px-2.5 py-2 shadow-card-subtle">
              <img
                src={OutlookImage}
                alt="Outlook"
                className="w-7 h-7 shrink-0 object-contain"
              />
              <div className="flex flex-col gap-1 flex-1 min-w-0">
                <div className="flex flex-col">
                  <span className="text-md font-medium text-verba-indigo dark:text-verba-indigo-dk leading-normal">
                    Outlook Integration
                  </span>
                  <span className="text-xs font-normal text-data dark:text-data-dk leading-normal">
                    Send emails using your Outlook mailbox
                  </span>
                </div>
                {outlookIntegration?.integration_status ? (
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-1 bg-pure-white dark:bg-pure-white-dk border border-border-gray dark:border-border-gray-dk rounded-md px-1.5 py-0.5 w-fit shadow-xs">
                      <span className="inline-block bg-verba-green-success dark:bg-verba-green-success-dk rounded-full w-2 h-2" />
                      <span className="text-md font-medium text-verba-indigo dark:text-verba-indigo-dk leading-[18px]">
                        Connected
                      </span>
                    </span>
                    <div className="relative" ref={threeDotMenuRef}>
                      <button
                        type="button"
                        className="p-0.5 rounded hover:bg-hover-effect dark:hover:bg-hover-effect-dk cursor-pointer"
                        onClick={() => setShowThreeDotMenu((prev) => !prev)}
                      >
                        <ThreeDotsVerticalIcon className="w-4 h-4 text-data dark:text-data-dk" />
                      </button>
                      {showThreeDotMenu && (
                        <div className="absolute right-0 top-full mt-1 bg-pure-white dark:bg-pure-white-dk border border-border-gray dark:border-border-gray-dk rounded-lg shadow-lg z-10 min-w-[180px]">
                          <button
                            type="button"
                            className="w-full flex items-center gap-1.5 px-3 py-2 text-left hover:bg-hover-effect dark:hover:bg-hover-effect-dk rounded-lg cursor-pointer"
                            onClick={() => {
                              setShowThreeDotMenu(false);
                              setShowDisconnectModal(true);
                            }}
                          >
                            <CrossIcon className="w-4 h-4 text-red-500" />
                            <span className="text-md font-medium text-red-500">
                              Disconnect Outlook
                            </span>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ) : outlookConnectStatus === "connecting" ? (
                  <span className="flex items-center gap-1">
                    <CircularLoader
                      size={14}
                      thickness={2}
                      className="text-gray-placeholder dark:text-gray-placeholder-dk"
                    />
                    <span className="text-md font-medium text-gray-placeholder dark:text-gray-placeholder-dk whitespace-nowrap">
                      Connecting...
                    </span>
                  </span>
                ) : outlookConnectStatus === "failed" ? (
                  <span className="flex items-center gap-2">
                    <span className="flex items-center gap-1 bg-pure-white dark:bg-pure-white-dk border border-border-gray dark:border-border-gray-dk rounded-md px-1.5 py-0.5 shadow-xs">
                      <CrossIcon className="w-3 h-3 text-red-500" />
                      <span className="text-md font-medium text-verba-indigo dark:text-verba-indigo-dk leading-[18px]">
                        Failed
                      </span>
                    </span>
                    <span
                      className="text-md font-semibold text-verba-blue dark:text-verba-blue-dk whitespace-nowrap hover:underline cursor-pointer"
                      onClick={handleConnectOutlook}
                    >
                      {"Retry >"}
                    </span>
                  </span>
                ) : (
                  <span
                    className="text-[12px] font-semibold text-verba-blue dark:text-verba-blue-dk cursor-pointer hover:underline"
                    onClick={handleConnectOutlook}
                  >
                    {"Connect >"}
                  </span>
                )}
              </div>
            </div>
          </>
        )}
        <DisconnectOutlookModal
          isOpen={showDisconnectModal}
          onClose={() => setShowDisconnectModal(false)}
          onConfirm={handleDisconnectOutlook}
          isLoading={isDisconnecting}
        />

        <div className="border-t border-gray-25 dark:border-gray-25-dk my-1" />

        <div className="flex justify-end gap-2">
          <Button
            innerText="Cancel"
            buttonContainerClass="py-1 px-7.5 border border-verba-indigo dark:border-verba-indigo-dk rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            innerTextClass="text-md font-medium whitespace-nowrap text-heading-text dark:text-heading-text-dk"
            onClick={() => setShowEditProfile(false)}
          />
          <Button
            innerText="Save"
            type="submit"
            isLoading={isLoading || isSubmitting}
            disabled={
              Object.keys(getUpdatedFields(values))?.length === 0 ||
              Object.keys(errors)?.length > 0
            }
            buttonContainerClass="py-1 px-7.5 rounded-lg bg-verba-indigo dark:bg-verba-indigo-dk disabled:opacity-50 disabled:cursor-not-allowed"
            innerTextClass="text-md font-medium whitespace-nowrap text-pure-white dark:text-pure-white-dk"
          />
        </div>
      </form>
    </div>
  );
};

export default EditProfile;
